import React from 'react';
import { useTranslation } from 'react-i18next';
import VerifiedBadge from '../../UI/VerifiedBadge';
import { COOLDOWN_DAYS } from '../../../constants';

function VerificationForm({
  verificationReason,
  setVerificationReason,
  verificationIdPreview,
  hasIdFile,
  handleVerificationIdSelect,
  verificationFileRef,
  handleVerificationSubmit,
  submittingVerification,
  setVerificationIdFile,
  setVerificationIdPreview,
}) {
  const { t } = useTranslation();
  return (
    <form className="epVerificationForm" onSubmit={handleVerificationSubmit}>
      <div className="epField">
        <label htmlFor="verification-reason" className="epLabel">{t('verification.whyVerify')}</label>
        <textarea
          id="verification-reason"
          className="epInput epTextarea"
          value={verificationReason}
          onChange={(e) => setVerificationReason(e.target.value)}
          placeholder={t('verification.whyVerifyPlaceholder')}
          rows={3}
          required
        />
      </div>
      <div className="epField">
        <label htmlFor="verification-id-doc" className="epLabel">{t('verification.idDocument')} <span className="epLabelRequired">*</span></label>
        <p className="epHintMuted">{t('verification.idDocumentHint')}</p>
        {verificationIdPreview ? (
          <div className="epIdPreview">
            <img src={verificationIdPreview} alt={t('verification.idPreview')} />
            <button
              type="button"
              className="epIdPreviewRemove"
              onClick={() => {
                setVerificationIdFile(null);
                setVerificationIdPreview(null);
                if (verificationFileRef.current) verificationFileRef.current.value = '';
              }}
              aria-label={t('verification.removeIdImage')}
            >
              &#10005;
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="epUploadBtn epUploadBtnRequired"
            onClick={() => verificationFileRef.current && verificationFileRef.current.click()}
          >
            {t('verification.uploadId')}
          </button>
        )}
        <input
          id="verification-id-doc"
          ref={verificationFileRef}
          type="file"
          accept="image/*"
          onChange={handleVerificationIdSelect}
          className="epHiddenInput"
        />
      </div>
      <button
        type="submit"
        className="epBtn epBtnAccent epBtnLarge"
        disabled={submittingVerification || !hasIdFile}
        title={!hasIdFile ? t('verification.uploadIdFirst') : ''}
      >
        {submittingVerification ? t('verification.submitting') : t('verification.requestBadge')}
      </button>
      {!hasIdFile && <p className="epHintError">{t('verification.uploadIdToSubmit')}</p>}
    </form>
  );
}

function VerificationFormWrapper({
  isVerified,
  vStatus,
  vData,
  canReapply,
  cooldownActive,
  daysRemaining,
  submittingVerification,
  verificationReason,
  setVerificationReason,
  verificationIdPreview,
  verificationIdFile,
  handleVerificationIdSelect,
  verificationFileRef,
  handleVerificationSubmit,
  setVerificationIdFile,
  setVerificationIdPreview,
}) {
const { t } = useTranslation();
  if (isVerified || vStatus === 'approved') {
    return (
      <div className="verificationStatusCard verificationApproved">
        <VerifiedBadge size={24} />
        <div>
          <strong>{t('verification.accountVerified')}</strong>
          <p>{t('verification.buyersCanSeeBadge')}</p>
          <p className="verificationPrivacyNote">{t('verification.idWillBeDeleted')}</p>
        </div>
      </div>
    );
  }
  if (vStatus === 'pending') {
    return (
      <div className="verificationStatusCard verificationPending">
        <span className="verificationStatusIcon">&#9203;</span>
        <div>
          <strong>{t('verification.requestPending')}</strong>
          <p>{t('verification.beingReviewed', { days: COOLDOWN_DAYS })}</p>
          <p className="verificationSubmittedDate">
            {t('verification.submitted')}: {vData?.createdAt
              ? new Date(vData.createdAt.seconds * 1000).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' })
              : t('verification.justNow')}
          </p>
        </div>
      </div>
    );
  }
  if (vStatus === 'rejected') {
    return (
      <>
        <div className="verificationStatusCard verificationRejected">
          <span className="verificationStatusIcon verificationRejectedIcon">&#10005;</span>
          <div>
            <strong>{t('verification.requestDeclined')}</strong>
            <p>
              {t('verification.notApproved')}
              {vData?.adminNote && <> {t('verification.reason')}: <em>{vData.adminNote}</em></>}
              {canReapply ? ` ${t('verification.submitNewRequest')}` : ` ${t('verification.reapplyInDays', { count: daysRemaining })}`}
            </p>
          </div>
        </div>
        {cooldownActive && (
          <div className="verificationActions">
            <span className="verificationCooldownBadge">{t('verification.daysRemaining', { days: daysRemaining })}</span>
          </div>
        )}
        {canReapply && (
          <VerificationForm
            verificationReason={verificationReason}
            setVerificationReason={setVerificationReason}
            verificationIdPreview={verificationIdPreview}
            hasIdFile={!!verificationIdFile}
            handleVerificationIdSelect={handleVerificationIdSelect}
            verificationFileRef={verificationFileRef}
            handleVerificationSubmit={handleVerificationSubmit}
            submittingVerification={submittingVerification}
            setVerificationIdFile={setVerificationIdFile}
            setVerificationIdPreview={setVerificationIdPreview}
          />
        )}
      </>
    );
  }
  if (submittingVerification) {
    return (
      <div className="verificationStatusCard verificationPending">
        <span className="verificationStatusIcon">&#9203;</span>
        <div>
          <strong>{t('verification.submittingRequest')}</strong>
          <p>{t('verification.pleaseWait')}</p>
        </div>
      </div>
    );
  }
  return (
    <>
      <p className="verificationDescription">{t('verification.getBadgeDescription')}</p>
      <VerificationForm
        verificationReason={verificationReason}
        setVerificationReason={setVerificationReason}
        verificationIdPreview={verificationIdPreview}
        hasIdFile={!!verificationIdFile}
        handleVerificationIdSelect={handleVerificationIdSelect}
        verificationFileRef={verificationFileRef}
        handleVerificationSubmit={handleVerificationSubmit}
        submittingVerification={submittingVerification}
        setVerificationIdFile={setVerificationIdFile}
        setVerificationIdPreview={setVerificationIdPreview}
      />
      <p className="verificationNote">{t('verification.oneRequestNote', { days: COOLDOWN_DAYS })}</p>
    </>
  );
}

export default VerificationFormWrapper;
